import React, { useState } from 'react';
import { Palette, Layout, Hash } from 'lucide-react';
import ColorPicker from './ColorPicker';

interface ButtonConfig {
  count: number;
  theme: string;
  layout: string;
  colors: {
    primary: string;
    secondary: string;
    text: string;
  };
}

interface ButtonConfigurationProps {
  config: ButtonConfig;
  onChange: (updates: Partial<ButtonConfig>) => void;
}

const ButtonConfiguration: React.FC<ButtonConfigurationProps> = ({ config, onChange }) => {
  const themes = [
    { id: 'solid', name: 'Solid', description: 'Solid color buttons' },
    { id: 'gradient', name: 'Gradient', description: 'Gradient color buttons' },
    { id: 'outline', name: 'Outline', description: 'Outlined buttons' },
    { id: 'ghost', name: 'Ghost', description: 'Transparent buttons' }
  ];

  const layouts = [
    { id: 'horizontal', name: 'Horizontal', description: 'Buttons in a row' },
    { id: 'vertical', name: 'Vertical', description: 'Buttons stacked' },
    { id: 'grid', name: 'Grid', description: 'Buttons in a grid' },
    { id: 'floating', name: 'Floating', description: 'Floating action buttons' }
  ];

  const [localFontFamily, setLocalFontFamily] = useState<string>('Inter');
  const [localFontSize, setLocalFontSize] = useState<number>(14);

  // Apply a preset to the parent config so the Preview (right panel) updates
  const applyPreset = (preset: Partial<ButtonConfig>) => {
    onChange({
      // Directly spread top-level properties from the preset
      ...(preset.theme ? { theme: preset.theme } : {}),
      ...(preset.layout ? { layout: preset.layout } : {}),
      ...(typeof preset.count === 'number' ? { count: preset.count } : {}),
      // Merge nested colors safely
      ...(preset.colors ? { colors: { ...config.colors, ...preset.colors } } : {}),
    });
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Button Configuration</h3>
      
      {/* Button Count */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Hash className="w-4 h-4" />
          Number of Buttons
        </label>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onChange({ count: Math.max(1, config.count - 1) })}
            className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-md hover:bg-gray-50"
          >
            -
          </button>
          <span className="w-12 text-center font-medium">{config.count}</span>
          <button
            onClick={() => onChange({ count: Math.min(10, config.count + 1) })}
            className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-md hover:bg-gray-50"
          >
            +
          </button>
        </div>
      </div>

      {/* Theme Selection */}
      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Palette className="w-4 h-4" />
          Button Theme
        </label>
        <div className="grid grid-cols-2 gap-3">
          {themes.map((theme) => (
            <button
              key={theme.id}
              onClick={() => onChange({ theme: theme.id })}
              className={`p-3 border-2 rounded-lg text-left transition-colors ${
                config.theme === theme.id
                  ? 'border-blue-500 bg-orange-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-sm font-medium text-gray-900">{theme.name}</div>
              <div className="text-xs text-gray-500">{theme.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Layout Selection */}
      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Layout className="w-4 h-4" />
          Button Layout
        </label>
        <div className="grid grid-cols-2 gap-3">
          {layouts.map((layout) => (
            <button
              key={layout.id}
              onClick={() => onChange({ layout: layout.id })}
              className={`p-3 border-2 rounded-lg text-left transition-colors ${
                config.layout === layout.id
                  ? 'border-blue-500 bg-orange-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-sm font-medium text-gray-900">{layout.name}</div>
              <div className="text-xs text-gray-500">{layout.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Color Customization */}
      <div className="space-y-4">
        <h4 className="text-md font-medium text-gray-900">Color Customization</h4>
        <div className="grid grid-cols-1 gap-4">
          <ColorPicker
            label="Primary Color"
            value={config.colors.primary}
            onChange={(color) => onChange({ 
              colors: { ...config.colors, primary: color }
            })}
          />
          <ColorPicker
            label="Secondary Color"
            value={config.colors.secondary}
            onChange={(color) => onChange({ 
              colors: { ...config.colors, secondary: color }
            })}
          />
          <ColorPicker
            label="Text Color"
            value={config.colors.text}
            onChange={(color) => onChange({ 
              colors: { ...config.colors, text: color }
            })}
          />
        </div>
      </div>

      {/* Preview */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">Preview</label>
        <div className={`p-4 border border-gray-200 rounded-lg ${
          config.layout === 'horizontal' ? 'flex gap-2' :
          config.layout === 'vertical' ? 'flex flex-col gap-2' :
          config.layout === 'grid' ? 'grid grid-cols-2 gap-2' :
          'flex flex-col gap-2'
        }`}>
          {Array.from({ length: config.count }, (_, i) => (
            <button
              key={i}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                config.theme === 'solid' ? `text-white` :
                config.theme === 'gradient' ? `text-white bg-gradient-to-r` :
                config.theme === 'outline' ? `border-2` :
                'bg-transparent hover:bg-gray-100'
              }`}
              style={{
                backgroundColor: config.theme === 'solid' ? config.colors.primary :
                               config.theme === 'gradient' ? undefined :
                               config.theme === 'outline' ? 'transparent' : 'transparent',
                backgroundImage: config.theme === 'gradient' ? 
                  `linear-gradient(to right, ${config.colors.primary}, ${config.colors.secondary})` : undefined,
                borderColor: config.theme === 'outline' ? config.colors.primary : undefined,
                color: config.theme === 'outline' ? config.colors.primary : config.colors.text,
                fontFamily: localFontFamily,
                fontSize: `${localFontSize}px`
              }}
            >
              Button {i + 1}
            </button>
          ))}
        </div>
      </div>

      {/* Showcase - Examples of Button Types */}
      <div className="space-y-8">
        <h4 className="text-md font-medium text-gray-900">Choose any Button Type</h4>
        <p className="text-xs text-gray-500">Hey, just log in to keep going!</p>

        {/* Large pill pair */}
        <div
          className="p-2 border-2 border-blue-200 rounded-xl bg-orange-50/30 flex items-center gap-8 justify-center"
          onClick={() => applyPreset({
            theme: 'solid',
            layout: 'horizontal',
            count: 2,
            colors: { primary: '#2563EB', secondary: '#1D4ED8', text: '#FFFFFF' },
          })}
          role="button"
        >
          <button className="px-8 py-1 rounded-md bg-blue-600 text-white text-sm font-medium flex items-center gap-4 shadow-sm">
            STARLINE <span className="inline-block w-1.5 h-1.5 bg-white rounded-full"></span>
          </button>
          <button className="px-8 py-1 rounded-md bg-orange-700 text-white text-sm font-medium flex items-center gap-4 shadow-sm">
            JACKPOT <span className="inline-block w-1.5 h-1.5 bg-white rounded-full"></span>
          </button>
        </div>

        {/* Solid green chips */}
        <div
          className="flex items-center justify-center gap-4"
          onClick={() => applyPreset({
            theme: 'solid',
            layout: 'horizontal',
            count: 3,
            colors: { primary: '#16A34A', secondary: '#15803D', text: '#FFFFFF' },
          })}
          role="button"
        >
          <button className="px-5 py-1 rounded bg-green-600 text-white text-sm flex items-center gap-2">STARLINE</button>
          <button className="px-5 py-1 rounded bg-green-600 text-white text-sm flex items-center gap-2">GAMES</button>
          <button className="px-5 py-1 rounded bg-green-600 text-white text-sm flex items-center gap-2">JACKPOT</button>
        </div>

        {/* Outlined lavender */}
        <div
          className="grid grid-cols-2 gap-4"
          onClick={() => applyPreset({
            theme: 'outline',
            layout: 'grid',
            count: 4,
            colors: { primary: '#A78BFA', secondary: config.colors.secondary, text: '#7C3AED' },
          })}
          role="button"
        >
          <button className="px-6 py-2 rounded-lg border-2 border-purple-300 text-purple-700 text-sm font-medium flex items-center justify-center gap-2 bg-white">STARLINE</button>
          <button className="px-6 py-2 rounded-lg border-2 border-purple-300 text-purple-700 text-sm font-medium flex items-center justify-center gap-2 bg-white">JACKPOT</button>
          <button className="px-6 py-2 rounded-lg border-2 border-purple-300 text-purple-700 text-sm font-medium flex items-center justify-center gap-2 bg-white">GAMES</button>
          <button className="px-6 py-2 rounded-lg border-2 border-purple-300 text-purple-700 text-sm font-medium flex items-center justify-center gap-2 bg-white">Live Updates</button>
        </div>

        {/* Tab bar dark */}
        <div
          className="grid grid-cols-3 text-center rounded overflow-hidden border border-gray-300"
          onClick={() => applyPreset({
            theme: 'solid',
            layout: 'horizontal',
            count: 3,
            colors: { primary: '#111827', secondary: '#374151', text: '#FFFFFF' },
          })}
          role="button"
        >
          <div className="py-2 bg-gray-700 text-white text-xs">Starline</div>
          <div className="py-2 bg-gray-800 text-white text-xs">Games</div>
          <div className="py-2 bg-gray-700 text-white text-xs">Jackpot</div>
        </div>

        {/* Red pills row */}
        <div
          className="flex items-center justify-center gap-6"
          onClick={() => applyPreset({
            theme: 'solid',
            layout: 'horizontal',
            count: 4,
            colors: { primary: '#DC2626', secondary: '#B91C1C', text: '#FFFFFF' },
          })}
          role="button"
        >
          {['STARLINE','GAMES','JACKPOT','LIVE UPDATES'].map((t)=> (
            <button key={t} className="px-4 py-1.5 rounded-full bg-red-600 text-white text-xs font-medium">{t}</button>
          ))}
        </div>

        {/* Segmented rounded control (like screenshot) */}
        <div
          className="w-full flex justify-center"
          onClick={() => applyPreset({
            theme: 'outline',
            layout: 'horizontal',
            count: 3,
            colors: { primary: config.colors.primary, secondary: config.colors.secondary, text: config.colors.text },
          })}
          role="button"
        >
          <div className="flex items-stretch w-[520px] max-w-full border-2 border-blue-600 rounded-full overflow-hidden">
            <button className="flex-1 flex items-center justify-center gap-3 py-1 text-base font-medium text-blue-900 bg-white" style={{fontFamily: localFontFamily, fontSize: `${localFontSize}px`}}>
              <span>Starline</span>
              <span className="inline-block w-0 h-0 border-t-6 border-b-6 border-l-8 border-t-transparent border-b-transparent border-l-blue-600" style={{borderTopWidth:'6px',borderBottomWidth:'6px',borderLeftWidth:'8px'}}></span>
            </button>
            <div className="w-px bg-blue-600" />
            <button className="flex-1 flex items-center justify-center gap-3 py-1 text-base font-medium text-blue-900 bg-white" style={{fontFamily: localFontFamily, fontSize: `${localFontSize}px`}}>
              <span>Games</span>
              <span className="text-blue-600 text-xl">🎲</span>
            </button>
            <div className="w-px bg-blue-600" />
            <button className="flex-1 flex items-center justify-center gap-3 py-1 text-base font-medium text-blue-900 bg-white" style={{fontFamily: localFontFamily, fontSize: `${localFontSize}px`}}>
              <span>Jackpot</span>
              <span className="text-blue-600 text-xl">🎯</span>
            </button>
          </div>
        </div>

        {/* Choose Colors (mirrors style from other pages) */}
        <div className="grid grid-cols-1 gap-4">
          <ColorPicker
            label="Primary Color"
            value={config.colors.primary}
            onChange={(color) => onChange({ colors: { ...config.colors, primary: color } })}
          />
          <ColorPicker
            label="Secondary Color"
            value={config.colors.secondary}
            onChange={(color) => onChange({ colors: { ...config.colors, secondary: color } })}
          />
          <ColorPicker
            label="Text Color"
            value={config.colors.text}
            onChange={(color) => onChange({ colors: { ...config.colors, text: color } })}
          />
        </div>

        {/* Choose Fonts */}
        <div className="space-y-2">
          <h4 className="text-md font-medium text-gray-900">Choose Fonts</h4>
          <p className="text-xs text-gray-500">Hey, just log in to keep going!</p>
          <div className="flex items-center gap-3">
            <select
              value={localFontFamily}
              onChange={(e)=>setLocalFontFamily(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              {['Inter','Arial','Roboto','Poppins','Nunito'].map(f=> (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
            <select
              value={localFontSize}
              onChange={(e)=>setLocalFontSize(parseInt(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              {[12,14,16,18].map(s=> (
                <option key={s} value={s}>{s} px</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ButtonConfiguration;
